import { FC, useState, useEffect, MouseEvent, KeyboardEvent, FormEvent } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '@hooks/useAppDispatch'
import { bulkCreatePermissions } from '../../actions'
import useDynamicAuthorization from '@hooks/useDynamicAuthorization'
import PermissionRoles from '@constants/permission_roles'
import Status from '@constants/request_statuses'
import HorizontalSpinner from '@components/HorizontalSpinner'
import Form from '@components/forms/form'
import FormGroup from '@components/forms/form_group'
import Label from '@components/forms/label'
import Input from '@components/forms/input'
import Select from '@components/forms/select'
import Button from '@components/Button'
import SVGIcon from '@components/SVGIcon'
import { toastSuccess, toastError } from '@actions/toast_actions'
import SpinnerButton from '@components/forms/spinner_button'
import UpgradePrompt from '@components/UpgradePrompt'
import { getRoleSelectOptions } from '../../utils'
import {
  selectBulkCreatePermissionsStatus,
  selectBulkCreatePermissionsError,
  selectBulkCreatePermissionsMessage,
} from '../../reducers'
import { StandardFieldError } from '@app-types/general'

type Props = {
  resourceId: string | number
  resourceType: string
}

type ChangeObject = {
  name: string
  value: string
}

type FormState = {
  username: string
  role: 'Viewer' | 'Editor' | 'Admin'
}

type ValidationErrors = {
  username?: string[]
  role?: string[]
}

const UserPermissionsStep: FC<Props> = props => {
  const { resourceId, resourceType } = props

  const bulkCreatePermissionsStatus = useSelector(selectBulkCreatePermissionsStatus)
  const bulkCreatePermissionsError = useSelector(selectBulkCreatePermissionsError)
  const bulkCreatePermissionsMessage = useSelector(selectBulkCreatePermissionsMessage)

  // This tracks data in the input/select fields
  // Note: When the user types in a username and hits enter, the username is added to the usernames array
  const [formData, setFormData] = useState<FormState>({
    username: '',
    role: PermissionRoles.VIEWER,
  })
  const [usernames, setUsernames] = useState<string[]>([])
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})
  const dispatch = useAppDispatch()

  // dynamic abilities
  const {
    can,
    status: abilitiesStatus,
    refreshAbilities,
  } = useDynamicAuthorization({
    resourceId,
    resourceType,
    abilities: ['read_permissions', 'grant_user_permissions', 'can_collaborate_feature_ability'],
  })

  useEffect(() => {
    refreshAbilities()
  }, [])

  useEffect(() => {
    if (bulkCreatePermissionsStatus === Status.SUCCEEDED && bulkCreatePermissionsMessage) {
      toastSuccess(bulkCreatePermissionsMessage)
    }

    if (bulkCreatePermissionsStatus === Status.FAILED && bulkCreatePermissionsError) {
      toastError(bulkCreatePermissionsError.message)
    }
  }, [bulkCreatePermissionsStatus, bulkCreatePermissionsError, bulkCreatePermissionsMessage])

  const getUpdatedUsernames = (currentUsernames: string[], newUsernames: string): string[] => {
    if (newUsernames.trim() === '') return currentUsernames

    // allow for comma separated usernames
    const usernamesToAdd = Array.from(
      new Set(
        newUsernames
          .split(',') // split on comma
          .map(username => username.trim()) // remove whitespace
          .filter(username => username && !currentUsernames.includes(username)) // remove empty strings or ones already in the list
      )
    )

    return [...currentUsernames, ...usernamesToAdd]
  }

  const handleSubmit = (
    event: SubmitEvent | MouseEvent<HTMLButtonElement, MouseEvent> | FormEvent<HTMLButtonElement>
  ) => {
    event.preventDefault()

    // Process the current input field but store the updated usernames locally
    const updatedUsernames = getUpdatedUsernames(usernames, formData.username)

    // Local validations using the updated usernames
    if (updatedUsernames.length === 0) {
      setValidationErrors({ username: ['At least one username is required'] })
      return
    }

    const { role } = formData
    const data = {
      permissionable_id: resourceId,
      permissionable_type: resourceType,
      permissable_identifiers: updatedUsernames,
      permissable_type: 'User',
      role,
    }
    dispatch(bulkCreatePermissions(data))

    // Clear the username input field after submission
    clearUsernameField()

    // Update the usernames state after the dispatch
    setUsernames(updatedUsernames)
  }

  const handleChange = (obj: ChangeObject) => {
    const { name, value } = obj
    setFormData({
      ...formData,
      [name]: value,
    })
  }

  const handleUsernameKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      processUsernamesInput()
    }
  }
  const handleAddUsernameClick = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault()
    processUsernamesInput()
  }

  const processUsernamesInput = () => {
    if (formData.username.trim() !== '') {
      const updatedUsernames = getUpdatedUsernames(usernames, formData.username)
      setUsernames(updatedUsernames)
      clearUsernameField()
      setValidationErrors({})
    }
  }

  // removes username from usernames array
  const removeUsername = (username: string) => {
    setUsernames(usernames.filter(name => name !== username))
  }

  const clearUsernameField = () => {
    setFormData({ ...formData, username: '' })
  }

  const getUsernamesContent = () => (
    <div className="tags">
      {usernames.map(username => (
        <span key={username} className="tags__tag tag tag--pill tag--removeable">
          <span>{username}</span>
          <Button className="button-reset remove-button" onClick={() => removeUsername(username)}>
            <SVGIcon xlinkHref="#streamline-delete-1" title="Remove" alt="Remove" className="icon" />
          </Button>
        </span>
      ))}
    </div>
  )

  const getErrorMessagesList = () => {
    if (bulkCreatePermissionsError?.fieldErrors) {
      const errorMessages: string[] = bulkCreatePermissionsError.fieldErrors.map((error: StandardFieldError) => {
        return error.detail
      })
      if (errorMessages.length > 0) {
        return (
          <ul>
            {errorMessages.map((errorMessage, index) => (
              <li key={index}>{errorMessage}</li>
            ))}
          </ul>
        )
      }
    }
    return null
  }

  const getMessagesContent = () => {
    let content = null
    if (bulkCreatePermissionsStatus === Status.SUCCEEDED && bulkCreatePermissionsMessage) {
      if (bulkCreatePermissionsError) {
        // we also had some errors, so partial success
        content = (
          <div className={`notice notice--mini notice--info p`}>
            {bulkCreatePermissionsMessage}
            {getErrorMessagesList()}
          </div>
        )
      } else {
        // no errors, full success
        content = <div className={`notice notice--mini notice--success p`}>{bulkCreatePermissionsMessage}</div>
      }
    } else if (bulkCreatePermissionsStatus === Status.FAILED && bulkCreatePermissionsError) {
      content = (
        <>
          <div className="error-explanation p">
            {bulkCreatePermissionsError.message}
            {getErrorMessagesList()}
          </div>
        </>
      )
    }
    return content
  }

  const getPermissionFormContent = () => {
    const roleSelectOptions = getRoleSelectOptions()
    const submitDisabled = bulkCreatePermissionsStatus === Status.LOADING
    return (
      <>
        <p>Assign permissions to specific users by entering their usernames and choosing an access level.</p>
        <Form onSubmit={handleSubmit} className="form form--limited-inputs sharing__permission-granter">
          <FormGroup className="form-group" validationErrors={validationErrors.username}>
            <Label className="label" htmlFor="username">
              Enter one or more usernames
            </Label>
            <div className="username-input-container">
              {getUsernamesContent()}
              <div className="username-input-container__controls">
                <Input
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  onKeyDown={handleUsernameKeyPress}
                  className="input username-input-container__input"
                  placeholder={'E.g. alice, bob, charlie'}
                />
                <Button className="btn btn--outline" onClick={handleAddUsernameClick}>
                  Add
                </Button>
              </div>
            </div>
          </FormGroup>

          <FormGroup className="form-group">
            <Label className="label" htmlFor="role">
              Choose access level
            </Label>
            <Select
              id="role"
              name="role"
              className="select"
              options={roleSelectOptions}
              value={formData.role}
              onChange={handleChange}
            />
          </FormGroup>

          {getMessagesContent()}

          <FormGroup className="form-group">
            <SpinnerButton className="btn btn--primary" onClick={handleSubmit} disabled={submitDisabled}>
              Grant access
            </SpinnerButton>
          </FormGroup>
        </Form>
      </>
    )
  }

  const getContent = () => {
    const canReadPermissions = can('read_permissions')
    const canGrantUserPermissions = can('grant_user_permissions')
    const canCollaborateFeatureAbility = can('can_collaborate_feature_ability')

    //console.log("Abilities: ", {canReadPermissions, canGrantUserPermissions, canCollaborateFeatureAbility})

    if (canCollaborateFeatureAbility) {
      if (canReadPermissions && canGrantUserPermissions) {
        return getPermissionFormContent()
      } else {
        return <div>You don’t have permission to grant access to this content.</div>
      }
    } else {
      return (
        <UpgradePrompt
          title="Upgrade your plan to grant access to individual users."
          ctaText="Upgrade now"
          ctaPath="/plans"
        />
      )
    }
  }

  return (
    <div className="p">
      {abilitiesStatus === Status.LOADING && <HorizontalSpinner size="large" />}

      {abilitiesStatus === Status.FAILED && <div>Error loading data.</div>}

      {abilitiesStatus === Status.SUCCEEDED && getContent()}
    </div>
  )
}

export default UserPermissionsStep
